import React, { useContext, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { api } from "api";
import { useArtistContext } from "Components";
import {
  ArtistEventContextProps,
  buildClickDetails,
  useArtistEventContext,
} from "./ArtistEventContext";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { doc, DocumentReference, getFirestore } from "firebase/firestore";
import { EventConfig, SelectField } from "@max/common";
import { UtmDocument, Vote } from "@max/common/src/analytics";
import { useQuery } from "react-query";
import { useDebounce } from "hooks/useDebounce";

export interface Stats {
  entries: number;
  visits: number;
  uniqueVisits: number;
  clicks: number;
}

interface Entry extends Stats {
  datetime: string;
}

export type multiDayReport = {
  entries: Entry[];
  entriesBySource: Record<string, number>;
  conversionBySource: Record<string, number>;
} & Required<
  Pick<
    ArtistEventContextProps,
    "votes" | "entriesTotals" | "entriesZips" | "impressions" | "clicks"
  >
>;

interface Context {
  startDate: DateTime;
  startDateInputValue: DateTime;
  setStartDate: (date: DateTime) => void;
  endDate: DateTime;
  endDateInputValue: DateTime;
  setEndDate: (date: DateTime) => void;
  multidayEvent: Record<string, any> | undefined;
  multidayEventLoading: boolean;
  utm: UtmDocument | undefined;
  utmDocReference: DocumentReference<UtmDocument>;
  multiDayReport: multiDayReport | undefined;
  multiDayReportLoading: boolean;
  multiDayReportRefetching: boolean;
  multiDayReportError: unknown;
  refetchMultiDayReport: VoidFunction;
}

const MultiDayEventContext = React.createContext<Context | undefined>(
  undefined,
);

const DELAY_TIME = 1_000;

export const MultiDayEventProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [startDate, setStartDate] = useState<DateTime>(
    DateTime.now().minus({ days: 6 }).startOf("day"),
  );
  const debouncedStartDate = useDebounce(startDate, DELAY_TIME);
  const [endDate, setEndDate] = useState<DateTime>(DateTime.now().endOf("day"));
  const debouncedEndDate = useDebounce(endDate, DELAY_TIME);
  const { id: artistGroupId, secureEnabled } = useArtistContext();
  const { eventId } = useArtistEventContext();
  const utmDocReference = doc(
    getFirestore(),
    `/set_fresh_events/${eventId}/config/utm`,
  ) as DocumentReference<UtmDocument>;
  const [utm] = useDocumentData(utmDocReference);
  const [multidayEvent, multidayEventLoading] = useDocumentData(
    eventId &&
      secureEnabled &&
      doc(getFirestore(), `set_fresh_events_multiday/${eventId}`),
  );
  const [version] = useDocumentData(
    eventId &&
      (doc(
        getFirestore(),
        `set_fresh_events/${eventId}/versions/en`,
      ) as DocumentReference<EventConfig>),
  );

  const report = useQuery({
    enabled: !!multidayEvent,
    cacheTime: 0,
    queryKey: [
      "multiDayReport",
      artistGroupId,
      eventId,
      debouncedStartDate,
      debouncedEndDate,
    ],
    queryFn: async ({ signal }) => {
      const response = await api.call(
        "multiday.report",
        {
          artistGroupId,
          eventId,
          start: debouncedStartDate
            .toUTC()
            .toISO({ suppressMilliseconds: false }),
          end: debouncedEndDate.toUTC().toISO(),
        },
        {
          signal,
        },
      );

      return response.data;
    },
  });

  const multiDayReport = useMemo(() => {
    if (!report.data) {
      return undefined;
    }

    const data: multiDayReport = {
      entries: report.data.entries,
      entriesBySource: utm?.enabled
        ? report.data.entriesBySource.reduce(
            (map, source) => ({
              ...map,
              [utm.labels?.[source.source_name] || source.source_name]:
                source.count,
            }),
            {},
          )
        : {},
      conversionBySource: utm?.enabled
        ? report.data.conversionRateBySource.reduce(
            (map, source) => ({
              ...map,
              [utm.labels?.[source.source_name] || source.source_name]:
                source.count,
            }),
            {},
          )
        : {},
      votes: Object.entries(
        report.data.customQuestions.reduce(
          (map, question) => ({
            ...map,
            [question.question]: {
              ...map[question.question],
              votes: {
                ...map[question.question]?.votes,
                [question.answer]: question.count,
              },
            },
          }),
          {},
        ) as Record<string, Vote>,
      ).reduce((map, [questionKey, questionData]) => {
        const questionField = version?.register?.customFields.find(
          (field) => field.id === questionKey,
        ) as SelectField;
        return {
          ...map,
          [questionKey]: {
            ...questionData,
            label: questionField?.label || questionKey,
            options: questionField.options.reduce(
              (map, option) => ({
                ...map,
                [option.value]: option.label,
              }),
              {},
            ),
          },
        };
      }, {}),
      entriesTotals: {
        all:
          report.data.entriesByLocation[0]?.confirmed_at_event +
          report.data.entriesByLocation[0]?.location_outside_event +
          report.data.entriesByLocation[0]?.location_not_shared,
        noLocation: report.data.entriesByLocation[0]?.location_not_shared,
        inVenue: report.data.entriesByLocation[0]?.confirmed_at_event,
      },
      entriesZips: report.data.fansByLocation,
      impressions: report.data.impressionsBySection.reduce(
        (map, section) => ({
          ...map,
          [section.label]: section.count,
        }),
        {},
      ),
      clicks: buildClickDetails(
        report.data.clickDetails.reduce(
          (map, click) => ({
            ...map,
            [click.type]: {
              ...map[click.type],
              [click.value]: click.count,
            },
          }),
          {},
        ),
      ),
    };

    return data;
  }, [report.data, utm, version]);

  const value: Context = {
    startDate: debouncedStartDate,
    startDateInputValue: startDate,
    setStartDate,
    endDate: debouncedEndDate,
    endDateInputValue: endDate,
    setEndDate,
    multidayEvent,
    multidayEventLoading,
    utm,
    utmDocReference,
    multiDayReport: multiDayReport,
    multiDayReportLoading: report.isLoading,
    multiDayReportRefetching: report.isFetching,
    multiDayReportError: report.error,
    refetchMultiDayReport: () => report.refetch(),
  };

  return (
    <MultiDayEventContext.Provider value={value}>
      {children}
    </MultiDayEventContext.Provider>
  );
};

export const useMultiDayEventContext = () => {
  const context = useContext(MultiDayEventContext);

  if (context === undefined) {
    throw new Error(
      "useMultiDayEventContext must be used within a useMultiDayEventProvider",
    );
  }

  return context;
};
